import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { classes, Option } from "@/utils"
import { endOfMonth, format, startOfMonth, startOfYear, subDays, subMonths, subYears } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { ComponentChildren } from "preact"
import { useRef, useState } from "preact/hooks"
import { DateRange } from "react-day-picker"
import { GlobalFilterState } from "@/global_filter/index"

interface DateRangeFilterProps {
  className?: string
}

export type Period = "currentMonth" | "previousMonth" | "past30Days" | "past60Days" | "past3Months" | "past90Days" | "past6Months" | "pastYear" | "ytd" | "past2Years" | "allTime"

interface QuickButtonProps {
  period: Period
  children: ComponentChildren
  handler: (period: Period) => void
}

function QuickButton({ period, children, handler }: QuickButtonProps) {
  return (
    <Button
      variant="outline"
      className="w-full justify-start text-left font-normal"
      // @ts-ignore
      size="sm"
      onClick={() => handler(period)}
    >
      {children}
    </Button>
  )
}

interface StartEnd {
    startDate: Option<Date>,
    endDate: Option<Date>,
}

export function getStartEnd(period: Period): StartEnd {
  const now = new Date()
  let start: Option<Date>
  let end: Option<Date>

  switch (period) {
    case "currentMonth":
      start = startOfMonth(now)
      end = endOfMonth(now)
      break
    case "previousMonth":
      start = startOfMonth(subMonths(now, 1))
      end = endOfMonth(subMonths(now, 1))
      break
    case "past30Days":
      start = subDays(now, 30)
      end = now
      break
    case "past60Days":
      start = subDays(now, 60)
      end = now
      break
    case "past3Months":
      start = subMonths(now, 3)
      end = now
      break
    case "past90Days":
      start = subDays(now, 90)
      end = now
      break
    case "past6Months":
      start = subMonths(now, 6)
      end = now
      break
    case "pastYear":
      start = subYears(now, 1)
      end = now
      break
    case "ytd":
      start = startOfYear(now)
      end = now
      break
    case "past2Years":
      start = subYears(now, 2)
      end = now
      break
    case "allTime":
      start = null
      end = null
      break
  }
  return {
    startDate: start,
    endDate: end,
  }
}

/** UX copy for the global date-range picker while selecting (exported for tests). */
export function getDateRangePickerHint(pendingRange: DateRange | undefined): string {
  if (pendingRange === undefined) {
    return "Tap two dates to set the range. Month and year menus jump the calendar view."
  }
  if (pendingRange.from && !pendingRange.to) {
    return "Step 2: Choose end date"
  }
  if (pendingRange.from && pendingRange.to) {
    return "Press Apply to confirm, or tap dates to adjust."
  }
  return "Step 1: Choose start date"
}

function commitRangeToGlobal(from: Date, to: Date) {
  GlobalFilterState.set(s => ({ ...s, startDate: from, endDate: to }))
}

export function DateRangeFilter({ className }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const [start, end] = GlobalFilterState.use(s => [s.startDate, s.endDate])
  // Track in-progress selection locally — Apply (or closing with a complete range) commits
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>()
  const [calendarMonth, setCalendarMonth] = useState<Date>(() =>
    startOfMonth(start ?? new Date())
  )
  /**
   * When true, `onOpenChange(false)` skips auto-commit (Apply and Cancel already handled persistence).
   * Outside-click close still commits a complete range when this is false.
   */
  const suppressCloseCommitRef = useRef(false)

  function handleSelect(range: DateRange | undefined) {
    setPendingRange(range)
  }

  function handleApply() {
    if (pendingRange?.from && pendingRange?.to) {
      commitRangeToGlobal(pendingRange.from, pendingRange.to)
    }
    suppressCloseCommitRef.current = true
    setPendingRange(undefined)
    setOpen(false)
  }

  function handleCancel() {
    suppressCloseCommitRef.current = true
    setPendingRange(undefined)
    setOpen(false)
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (nextOpen) {
      setCalendarMonth(startOfMonth(start ?? new Date()))
      return
    }
    if (suppressCloseCommitRef.current) {
      suppressCloseCommitRef.current = false
      return
    }
    if (pendingRange?.from && pendingRange?.to) {
      commitRangeToGlobal(pendingRange.from, pendingRange.to)
    }
    setPendingRange(undefined)
  }

  function handleQuickButton(period: Period) {
    let { startDate, endDate } = getStartEnd(period)
    GlobalFilterState.set(s => ({ ...s, startDate, endDate }))
    setPendingRange(undefined)
    setOpen(false)
  }

  // Show pending in-progress selection in the button label while the popover is open
  const displayStart = (open && pendingRange?.from) ? pendingRange.from : start
  const displayEnd = (open && pendingRange?.to) ? pendingRange.to : end
  const displayText = displayStart && displayEnd
    ? `${format(displayStart, "MM/dd/yyyy")} - ${format(displayEnd, "MM/dd/yyyy")}`
    : "Select date range"

  // Calendar shows the pending selection while open, otherwise the committed range
  const calendarSelected = pendingRange ?? { from: start ?? undefined, to: end ?? undefined }
  const canApply = Boolean(pendingRange?.from && pendingRange?.to)
  const hint = getDateRangePickerHint(pendingRange)

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={classes(
              "justify-start text-left font-normal",
              // @ts-ignore
              !start && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {displayText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto max-w-[min(95vw,56rem)] overflow-visible p-0" align="start">
          <div className="flex flex-col sm:flex-row sm:items-stretch">
            <div className="flex w-full shrink-0 flex-col gap-1 border-b border-border bg-muted/35 p-3 sm:w-44 sm:border-b-0 sm:border-r">
              <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Quick ranges
              </p>
              <QuickButton period="currentMonth" handler={handleQuickButton}>Current Month</QuickButton>
              <QuickButton period="previousMonth" handler={handleQuickButton}>Previous Month</QuickButton>
              <QuickButton period="past30Days" handler={handleQuickButton}>Past 30 Days</QuickButton>
              <QuickButton period="past60Days" handler={handleQuickButton}>Past 60 Days</QuickButton>
              <QuickButton period="past3Months" handler={handleQuickButton}>Past 3 Months</QuickButton>
              <QuickButton period="past6Months" handler={handleQuickButton}>Past 6 Months</QuickButton>
              <QuickButton period="ytd" handler={handleQuickButton}>YTD</QuickButton>
              <QuickButton period="pastYear" handler={handleQuickButton}>Past Year</QuickButton>
              <QuickButton period="past2Years" handler={handleQuickButton}>Past 2 Years</QuickButton>
              <QuickButton period="allTime" handler={handleQuickButton}>All Time</QuickButton>
            </div>
            <div className="flex min-w-0 min-h-0 flex-1 flex-col overflow-visible">
              <p className="border-b border-border/70 bg-muted/25 px-4 py-2 text-[11px] leading-snug text-muted-foreground">
                {hint}
              </p>
              <div className="flex justify-center overflow-x-auto px-1 pb-1">
                <Calendar
                  autoFocus
                  mode="range"
                  captionLayout="dropdown"
                  pagedNavigation={false}
                  startMonth={new Date(1990, 0)}
                  endMonth={new Date(2050, 11)}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  selected={calendarSelected}
                  onSelect={handleSelect}
                  numberOfMonths={start ? 2 : 1}
                  min={1}
                  max={500}
                />
              </div>
              <div className="mt-auto flex w-full flex-shrink-0 justify-end gap-2 border-t border-border bg-muted/25 px-4 py-3">
                <Button variant="outline" type="button" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleApply} disabled={!canApply}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
